// Modules
import Select from "react-select"
import { Controller, Control, useFormContext } from "react-hook-form"

// Constants
import { customSelectStyles } from "~constants/customSelectStyles"

// Components
import { ControlSelectContainer } from "~styled/select"

// Utils
import { SelectOption } from "~utils/dropdown"

interface SelectProps {
  name: string
  label?: string
  labelStyle?: {}
  rules?: any
  height?: number
  control: Control
  options: SelectOption[]
  isMulti?: boolean
  disabled?: boolean
  position?: "auto" | "top" | "bottom"
  className?: string
  isLoading?: boolean
  searchable?: boolean
  placeholder?: string
  errorMessage?: string
  marginBottom?: string
  isClearable?: boolean
}

const filterOptions = (candidate: SelectOption, input: string): boolean => {
  return !input || candidate.label.toLowerCase().includes(input.toLowerCase())
}

const ControlSelect = (props: SelectProps) => {
  const {
    name,
    label,
    labelStyle = {},
    rules,
    height,
    control,
    options,
    isMulti = false,
    disabled,
    position = "bottom",
    className,
    isLoading = false,
    searchable = true,
    placeholder,
    errorMessage,
    marginBottom = "20px",
    isClearable = false,
  } = props

  // Read the current form value directly from form state to ensure we always have the latest value
  // This fixes the issue where Controller's value prop becomes stale after form resets
  const formContext = useFormContext()
  const watchedValue = formContext ? formContext.watch(name) : undefined

  return (
    <ControlSelectContainer style={{ marginBottom }} className={className}>
      <span className="label" style={labelStyle}>
        {label}
      </span>

      <Controller
        name={name}
        rules={rules}
        control={control}
        render={({ field: { onChange, value } }) => {
          // Use watchedValue if Controller's value is undefined but form has a value
          // This handles the case where Controller loses sync with form state
          const effectiveValue = value !== undefined ? value : watchedValue

          // Normalize value to use exact option objects from options array
          // This fixes react-select multi-select reference matching issues
          let normalizedValue = effectiveValue
          if (isMulti && Array.isArray(value) && value.length > 0) {
            // For multi-select, map form values to exact option objects
            normalizedValue = value
              .map((val: any) => {
                const valueStr =
                  typeof val === "object" && "value" in val ? val.value : val
                return options.find(opt => opt.value === valueStr) || val
              })
              .filter(Boolean)
          } else if (value && !Array.isArray(value)) {
            // For single-select, find exact option object
            const valueStr =
              typeof value === "object" && "value" in value
                ? value.value
                : value
            normalizedValue =
              options.find(opt => opt.value === valueStr) || value
          }

          return (
            <Select
              value={normalizedValue}
              options={options}
              isDisabled={disabled || !options.length}
              className={errorMessage && "error"}
              isSearchable={searchable}
              placeholder={placeholder || ""}
              maxMenuHeight={height}
              menuPlacement={position}
              onChange={onChange}
              isLoading={isLoading}
              styles={customSelectStyles}
              components={{ IndicatorSeparator: null }}
              filterOption={filterOptions}
              isMulti={isMulti}
              isClearable={isClearable}
              getOptionValue={(option: SelectOption) => option.value}
              getOptionLabel={(option: SelectOption) => option.label}
              isOptionEqualToValue={(option: SelectOption, val: any) => {
                // Compare by value property, handling both object and string values
                if (!val) return false
                if (typeof val === "object" && "value" in val) {
                  return option.value === val.value
                }
                return option.value === val
              }}
              inputId={`${name}-input`}
            />
          )
        }}
      />

      <span className="error-message">{errorMessage}</span>
    </ControlSelectContainer>
  )
}

export default ControlSelect
